import { useEffect, useRef, useState } from 'react';

// iOS Safari never shrinks the layout viewport for the on-screen keyboard —
// 100dvh, interactive-widget=resizes-content and the VirtualKeyboard API all
// do nothing there. window.visualViewport is the only signal that tracks the
// keyboard, so the mobile takeover sizes itself from it (Discourse's
// d-virtual-height pattern: debounce, threshold, re-measure after blur).

export const VIEWPORT_RESIZE_EVENT = 'rw:viewport-resize';
const DEBOUNCE_MS = 50;
const KEYBOARD_THRESHOLD_PX = 150;
const LATE_REMEASURE_MS = 400;

export type ViewportGeometry = {
    top: number;
    height: number;
    keyboardOpen: boolean;
    supported: boolean;
};

function measure(): ViewportGeometry {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) {
        const height = typeof window !== 'undefined' ? window.innerHeight : 0;
        return { top: 0, height, keyboardOpen: false, supported: false };
    }
    const vvHeight = Math.round(vv.height);
    const keyboardOpen = window.innerHeight - vvHeight > KEYBOARD_THRESHOLD_PX;
    // iOS 26 leaves vv.height stale after the keyboard closes. With the keyboard
    // closed the visual viewport equals the layout viewport, so innerHeight wins.
    const height = keyboardOpen ? vvHeight : Math.max(vvHeight, window.innerHeight);
    return {
        top: Math.round(vv.offsetTop),
        height,
        keyboardOpen,
        supported: true,
    };
}

export function useVisualViewport(enabled: boolean): ViewportGeometry {
    const [geometry, setGeometry] = useState<ViewportGeometry>(measure);
    const lastHeight = useRef(geometry.height);

    useEffect(() => {
        if (!enabled) return undefined;

        let timer: ReturnType<typeof setTimeout> | undefined;
        let raf1: number | undefined;
        let raf2: number | undefined;
        let lateTimer: ReturnType<typeof setTimeout> | undefined;

        const apply = () => {
            const next = measure();
            setGeometry(next);
            if (next.height !== lastHeight.current) {
                lastHeight.current = next.height;
                window.dispatchEvent(new CustomEvent(VIEWPORT_RESIZE_EVENT));
            }
        };
        const scheduled = () => {
            clearTimeout(timer);
            timer = setTimeout(apply, DEBOUNCE_MS);
        };
        // iOS 26.0/26.1 leave a stale height/offset after the keyboard closes;
        // re-measuring two frames after the blur catches the settled values.
        const onFocusOut = () => {
            // Coalesce bursts: drop the previous chain so cleanup only ever
            // has the live one to cancel.
            if (raf1 !== undefined) cancelAnimationFrame(raf1);
            if (raf2 !== undefined) cancelAnimationFrame(raf2);
            raf2 = undefined;
            // The iOS keyboard dismissal animation takes ~250-300 ms and the vv
            // resize can fire before the values settle: one late re-measure.
            clearTimeout(lateTimer);
            lateTimer = setTimeout(scheduled, LATE_REMEASURE_MS);
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(scheduled);
            });
        };

        apply();
        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener('resize', scheduled);
            vv.addEventListener('scroll', scheduled);
        }
        // Safari toolbar collapse may only announce itself via window events.
        window.addEventListener('resize', scheduled);
        window.addEventListener('orientationchange', scheduled);
        document.addEventListener('focusout', onFocusOut);

        return () => {
            clearTimeout(timer);
            clearTimeout(lateTimer);
            if (raf1 !== undefined) cancelAnimationFrame(raf1);
            if (raf2 !== undefined) cancelAnimationFrame(raf2);
            if (vv) {
                vv.removeEventListener('resize', scheduled);
                vv.removeEventListener('scroll', scheduled);
            }
            window.removeEventListener('resize', scheduled);
            window.removeEventListener('orientationchange', scheduled);
            document.removeEventListener('focusout', onFocusOut);
        };
    }, [enabled]);

    return geometry;
}
